'use client'

import { X24, HomeFilled24 } from '@vinted/monochrome-icons'
import {
  Button,
  Cell,
  Dialog,
  Divider,
  Image,
  Label,
  Navigation,
  Spacer,
  Text,
  Icon,
} from '@vinted/web-ui'
import { ChangeEvent, createRef, RefObject, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { reloadPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  AcceptedFileTypes,
  MAXIMUM_FILE_SIZE_BYTES,
  MAXIMUM_FILE_SIZE_TEXT,
} from '@marketplace-web/item-upload/media-upload-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/shared/assets'
import { findThumbnail } from '@marketplace-web/shared/utils'
import {
  createPhoto,
  transformMediaUploadPhotoDto,
} from '@marketplace-web/item-upload/media-upload-data'

import { formatCurrency } from '@marketplace-web/currency/currency-data'
import {
  getShipmentInstructions,
  transformShipmentInstructionsResponse,
  markAsShipped,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'

const MAXIMUM_FILE_AMOUNT = 1

type Props = {
  transactionId: number
  onClose: () => void
}

const CustomShippingInstructionsModal = ({ transactionId, onClose }: Props) => {
  const translate = useTranslate('conversation.custom_shipping_instructions.modal')
  const asset = useAsset('assets/no-photo')
  const { locale } = useIntl()

  const [receiptFile, setReceiptFile] = useState<string | null | undefined>('')
  const [fileName, setFileName] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)

  const fileInput: RefObject<HTMLInputElement | null> = createRef()

  const {
    fetch: fetchShipmentInstructions,
    transformedData: shipmentInstructions,
    isLoading: isShipmentInstructionsLoading,
  } = useFetch(getShipmentInstructions, response => transformShipmentInstructionsResponse(response))

  useEffect(() => {
    if (!transactionId) return

    fetchShipmentInstructions({ transactionId })
  }, [transactionId, fetchShipmentInstructions])

  function renderTitle() {
    if (shipmentInstructions?.returnShippingOption) {
      return translate('return_title')
    }

    return translate('title')
  }

  function renderButtonText() {
    if (shipmentInstructions?.returnShippingOption) {
      return translate('actions.item_sent')
    }

    return translate('actions.mark_as_shipped')
  }

  async function handleMarkAsShipped() {
    if (!receiptFile) {
      setError(translate('validation_error'))

      return
    }

    setError('')

    setIsLoading(true)

    const response = await markAsShipped({
      transactionId,
      proofPhotoUuid: receiptFile,
    })

    if ('errors' in response) {
      setError(response.errors[0]?.value || response.message)

      return
    }

    setIsLoading(false)

    reloadPage()
  }

  async function uploadPhoto(file: File) {
    setIsLoading(true)

    const type = 'user_msg'
    const photo = new FormData()

    photo.append('photo[type]', type)
    photo.append('photo[file]', file)

    const response = await createPhoto(photo)

    if ('errors' in response) {
      setError(response.errors[0]?.value || response.message)

      return
    }

    setReceiptFile(transformMediaUploadPhotoDto(response).tempUuid)
    setFileName(file.name)
    setIsLoading(false)
  }

  function renderShipmentType() {
    if (!shipmentInstructions) return null

    const priceLabel = formatCurrency(shipmentInstructions.shippingPrice.amount, {
      currency: shipmentInstructions.shippingPrice.currencyCode,
      locale,
    })

    return (
      <>
        <Label type={Label.Type.Stacked} text={translate('label')} />
        <Cell
          prefix={<Icon name={HomeFilled24} color={Icon.Color.GreyscaleLevel2} />}
          title={<Text as="h2" text={translate('custom_shipping')} type={Text.Type.Title} />}
          suffix={<Text as="span" truncate text={priceLabel} />}
          testId="custom-shipping-instructions-modal-shipment-type-cell"
        />
      </>
    )
  }

  function renderReturnShipmentType() {
    if (!shipmentInstructions?.returnShippingOption) return null

    return (
      <>
        <Label type={Label.Type.Stacked} text={translate('label')} />
        <Cell
          prefix={
            <Image
              src={shipmentInstructions?.returnShippingOption.iconUrl}
              size={Image.Size.Medium}
              styling={Image.Styling.Rounded}
            />
          }
          title={
            <Text
              as="h2"
              text={shipmentInstructions?.returnShippingOption.title}
              type={Text.Type.Title}
            />
          }
          subtitle={<Text as="span" text={shipmentInstructions?.returnShippingOption.subtitle} />}
          testId="custom-shipping-instructions-modal-return-shipment-type-cell"
        />
      </>
    )
  }

  function renderAddress() {
    if (!shipmentInstructions) return null

    return (
      <>
        <Label type={Label.Type.Stacked} text={translate('address_field_label')} />
        <Cell
          prefix={
            <Image
              src={
                findThumbnail(shipmentInstructions.receiverPhoto) || asset(EMPTY_USER_IMAGE_NAME)
              }
              size={Image.Size.Medium}
              styling={Image.Styling.Circle}
            />
          }
          title={<Text as="h2" text={shipmentInstructions.receiverName} type={Text.Type.Title} />}
          body={<Text as="span" html text={shipmentInstructions.receiverFullAddress} />}
          testId="custom-shipping-instructions-modal-address-cell"
        />
      </>
    )
  }

  function showFileSelectionPrompt() {
    setError('')

    fileInput.current?.click()
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]

    if (!file) return

    if (file.size > MAXIMUM_FILE_SIZE_BYTES) {
      setError(translate('file_size_error', { file_size: MAXIMUM_FILE_SIZE_TEXT }))

      return
    }

    uploadPhoto(file)
  }

  function renderAttachShipmentReceipt() {
    return (
      <>
        <Label type={Label.Type.Stacked} text={translate('shipment_receipt_label')} />
        <Cell>
          <Cell
            styling={Cell.Styling.Tight}
            prefix={
              <>
                <input
                  ref={fileInput}
                  onChange={handleFileChange}
                  hidden
                  type="file"
                  accept={AcceptedFileTypes.Images}
                  max={MAXIMUM_FILE_AMOUNT}
                  data-testid="proof-file-uploader"
                />
                <Button
                  text={translate('actions.choose_file')}
                  size={Button.Size.Small}
                  inline
                  theme="muted"
                  onClick={showFileSelectionPrompt}
                  testId="custom-shipping-instructions-modal-add-file-button"
                />
              </>
            }
          >
            <div className="u-flexbox">
              <Text as="span" text={fileName} truncate />
            </div>
          </Cell>
          <div>
            <Text
              as="h4"
              type={Text.Type.Caption}
              theme="warning"
              text={error}
              testId="custom-shipping-instructions-modal-validation-error"
            />
          </div>
          <Spacer />
          <Cell styling={Cell.Styling.Tight}>
            <Button
              styling={Button.Styling.Filled}
              text={renderButtonText()}
              isLoading={isLoading}
              onClick={handleMarkAsShipped}
              testId="custom-shipping-instructions-modal-mark-as-shipped-button"
            />
          </Cell>
        </Cell>
      </>
    )
  }

  function renderLoader() {
    return (
      <ContentLoader
        styling={ContentLoader.Styling.Wide}
        testId="custom-shipping-instructions-modal-loader"
      />
    )
  }

  function renderModalContent() {
    if (isShipmentInstructionsLoading) return renderLoader()

    return (
      <SeparatedList separator={<Divider />}>
        {shipmentInstructions?.returnShippingOption
          ? renderReturnShipmentType()
          : renderShipmentType()}
        {renderAddress()}
        {renderAttachShipmentReceipt()}
      </SeparatedList>
    )
  }

  function handleModalClose() {
    setError('')
    onClose()
    setReceiptFile('')
    setFileName('')
  }

  return (
    <Dialog show closeOnOverlay testId="custom-shipping-instructions-modal">
      <Navigation
        right={
          <Button
            styling={Button.Styling.Flat}
            onClick={handleModalClose}
            iconName={X24}
            inline
            testId="custom-shipping-instructions-modal-close-button"
          />
        }
        body={renderTitle()}
      />
      <Divider />
      {renderModalContent()}
    </Dialog>
  )
}

export default CustomShippingInstructionsModal
